// src/components/PDF/InformeEvaluadorPDF.jsx
import React from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";

// 🎨 Estilos básicos del PDF
const styles = StyleSheet.create({
  page: { padding: 20, fontSize: 10, fontFamily: "Helvetica" },
  title: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 5,
    textAlign: "center",
  },
  subtitle: { fontSize: 12, fontWeight: "bold", marginBottom: 3 },
  section: {
    marginBottom: 10,
    borderBottom: "1pt solid #ccc",
    paddingBottom: 5,
  },
  text: { marginBottom: 3, textAlign: "justify" },
  tableHeader: {
    flexDirection: "row",
    borderBottom: "1pt solid #000",
    paddingVertical: 2,
  },
  tableRow: {
    flexDirection: "row",
    borderBottom: "0.5pt solid #ccc",
    paddingVertical: 2,
  },
  tableCell: { flex: 1, paddingHorizontal: 2 },
  bold: { fontWeight: "bold" },
});

// 🏗️ Tabla PDF
const Table = ({ columns = [], data = [] }) => (
  <View style={{ marginBottom: 10 }}>
    <View style={styles.tableHeader}>
      {columns.map((col, i) => (
        <Text key={i} style={[styles.tableCell, styles.bold]}>
          {col}
        </Text>
      ))}
    </View>
    {data && data.length > 0 ? (
      data.map((row, idx) => (
        <View key={idx} style={styles.tableRow}>
          {columns.map((col, j) => (
            <Text key={j} style={styles.tableCell}>
              {String(row[col] ?? "")}
            </Text>
          ))}
        </View>
      ))
    ) : (
      <Text style={styles.text}>No hay datos disponibles</Text>
    )}
  </View>
);

// 🔎 Agrupar tareas
const groupByTask = (tareas = []) => {
  const map = new Map();
  tareas.forEach((t) => {
    if (!map.has(t.nombretarea)) map.set(t.nombretarea, []);
    map.get(t.nombretarea).push(t);
  });
  return map;
};

// 📄 Componente principal
const InformeEvaluadorPDF = ({
  institucion = [],
  tipoSolicitud = [],
  fechas = [],
  tareas = [],
  repLegal = [],
  gerente = [],
  liderSolicitud = [],
  equipoUsuario = [],
  cuadroTrazador = [],
  encuestaUsuario = [],
  encuestaTrabajadores = [],
  morbilidad = [],
  mortalidad = [],
  tabla11 = [],
}) => {
  const tareasMap = groupByTask(tareas);
  const getTasks = (key) => tareasMap.get(key) || [];
  const equipoLider = equipoUsuario.filter((e) => e?.nombreRol === "LIDER");

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* ENCABEZADO */}
        <View style={styles.section}>
          <Text style={styles.title}>INFORME DEL EVALUADOR</Text>
          <Text style={styles.text}>
            Institución:{" "}
            {(institucion || []).map((i) => i.razon_Social).join(", ")}
          </Text>
          <Text style={styles.text}>
            Ciudad: {(institucion || []).map((i) => i.ciudad).join(", ")}
          </Text>
          {(fechas || []).map((f, idx) => (
            <Text key={idx} style={styles.text}>
              Fecha Evaluación: {f?.fechaInicio || "N/A"} -{" "}
              {f?.fechaFin || "N/A"}
            </Text>
          ))}
          <Text style={styles.text}>
            Representante Legal:{" "}
            {repLegal.length > 0 ? repLegal.at(-1)?.nombre : "No disponible"}
          </Text>
          <Text style={styles.text}>
            Gerente:{" "}
            {gerente.length > 0 ? gerente.at(-1)?.nombre : "No disponible"}
          </Text>
          <Text style={styles.text}>
            Líderes de Acreditación:{" "}
            {(liderSolicitud || []).map((l) => l.nombre).join(", ")}
          </Text>
          <Text style={styles.text}>
            Evaluador Líder:{" "}
            {(equipoLider || []).map((l) => l.nombre).join(", ")}
          </Text>
        </View>

        {/* TIPO DE SOLICITUD */}
        <View style={styles.section}>
          <Text style={styles.subtitle}>Tipo de Solicitud</Text>
          {(tipoSolicitud || []).map((item, idx) => (
            <Text key={idx} style={styles.text}>
              {item.tipoSolicitud}
            </Text>
          ))}
        </View>

        {/* ALCANCE */}
        <View style={styles.section}>
          <Text style={styles.subtitle}>Alcance</Text>
          {getTasks("Alcance").map((t, idx) => (
            <Text key={idx} style={styles.text}>
              {t.comentarios}
            </Text>
          ))}
        </View>

        {/* REUNIÓN DE APERTURA */}
        <View style={styles.section}>
          <Text style={styles.subtitle}>Reunión de Apertura</Text>
          {getTasks("Reunion de apertura").map((t, idx) => (
            <Text key={idx} style={styles.text}>
              {t.comentarios}
            </Text>
          ))}
        </View>

        {/* TABLAS */}
        <View style={styles.section}>
          <Text style={styles.subtitle}>6.1 Cuadro Trazador</Text>
          <Table
            columns={["Paciente", "Diagnóstico", "Resultado"]}
            data={cuadroTrazador}
          />
        </View>

        <View style={styles.section}>
          <Text style={styles.subtitle}>Encuesta a Usuarios</Text>
          <Table columns={["Pregunta", "Respuesta"]} data={encuestaUsuario} />
        </View>

        <View style={styles.section}>
          <Text style={styles.subtitle}>Encuesta a Trabajadores</Text>
          <Table
            columns={["Pregunta", "Respuesta"]}
            data={encuestaTrabajadores}
          />
        </View>

        <View style={styles.section}>
          <Text style={styles.subtitle}>Morbilidad</Text>
          <Table
            columns={["Código", "Descripción", "Casos"]}
            data={morbilidad}
          />
        </View>

        <View style={styles.section}>
          <Text style={styles.subtitle}>Mortalidad</Text>
          <Table
            columns={["Código", "Descripción", "Casos"]}
            data={mortalidad}
          />
        </View>

        <View style={styles.section}>
          <Text style={styles.subtitle}>Tabla 11</Text>
          <Table columns={["Campo1", "Campo2", "Campo3"]} data={tabla11} />
        </View>

        {/* ANALISIS INSTITUCIONAL */}
        <View style={styles.section}>
          <Text style={styles.subtitle}>Análisis Institucional</Text>
          {getTasks(
            "4.1 ANALISIS DE LOS PRINCIPALES CAMBIOS, AVANCES Y FORTALEZAS INSTITUCIONALES"
          ).map((t, idx) => (
            <Text key={idx} style={styles.text}>
              {t.comentarios}
            </Text>
          ))}
        </View>
      </Page>
    </Document>
  );
};

export default InformeEvaluadorPDF;
